<template>
  <div class="loader-wrapper">
    <div class="loader-copy">
      <div class="header-2 loader-description">
        {{ label }}
      </div>

      <div class="caption loader-description">
        {{ subtitle }}
      </div>
    </div>

    <template v-if="isProcessing">
      <div class="progress-placeholder">
        <div class="spinner">
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="progress-placeholder">
        <svg class="progress-circle" width="100" height="100">
          <defs>
            <radialGradient id="progress-gradient" cx="50%" cy="50%" r="50%">
              <stop offset="26%" stop-color="#20AD8B" />
              <stop offset="76%" stop-color="#ACE41B" />
            </radialGradient>
          </defs>
          <g transform="rotate(-90, 50, 50)">
            <circle class="progress-circle-bg" cx="50" cy="50" r="45" fill="transparent" />
            <circle class="progress-circle-fg" cx="50" cy="50" r="45" fill="transparent"
              stroke="url(#progress-gradient)" :stroke-dasharray="circumference"
              :stroke-dashoffset="circumference - (progress / 100) * circumference" stroke-linecap="round" />
          </g>
        </svg>
      </div>

      <div v-if="showSize" class="progress-label">
        {{ formattedLoadedSize }} / {{ formattedTotalSize }} ({{ Math.round(progress) }}%)
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  progress: {
    type: Number,
    required: true,
    default: 0
  },
  label: {
    type: String,
    default: ''
  },
  subtitle: {
    type: String,
    default: ''
  },
  isProcessing: {
    type: Boolean,
    default: false
  },
  loadedSize: {
    type: Number,
    default: 0
  },
  totalSize: {
    type: Number,
    default: 0
  },
  showSize: {
    type: Boolean,
    default: false
  }
});

const loaderRadius = 45;
const circumference = computed(() => 2 * Math.PI * loaderRadius);

const formattedLoadedSize = computed(() => formatSize(props.loadedSize));
const formattedTotalSize = computed(() => formatSize(props.totalSize));

function formatSize(size) {
  if (size < 1024) return `${size} B`;
  const kb = size / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
</script>

<style scoped>
.loader-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 50vh;
  width: min(100%, 420px);
  margin: 0 auto;
  text-align: center;
  padding-top: clamp(4px, 2vh, 20px);
  box-sizing: border-box;
}

.caption {
  color: var(--color-white-40);
  line-height: 1.4;
}

.loader-copy {
  width: 100%;
  min-height: 118px;
  display: grid;
  grid-template-rows: minmax(34px, auto) minmax(44px, auto);
  align-content: start;
}

.loader-description {
  width: 100%;
}

.header-2 {
  font-size: var(--font-size-header-alt);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 var(--spacing-md) 0;
}

.caption.loader-description {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin: 0 0 var(--spacing-md) 0;
}

.progress-placeholder {
  background-color: var(--color-white-10);
  border-radius: var(--border-radius-lg);
  padding: var(--margin-md);
  width: clamp(220px, 28vw, 320px);
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-clip: padding-box;
  background-position: center;
  border: var(--border-width-thicker) solid var(--color-white-10);
  box-sizing: border-box;
  flex-shrink: 0;
  margin-top: 0;
}

.progress-circle-bg {
  stroke: var(--border-color-light);
  stroke-width: var(--border-width-thick);
}

.progress-circle-fg {
  stroke-width: var(--border-width-thick);
  transition: stroke-dashoffset var(--transition-linear);
}

.progress-label {
  margin-top: var(--spacing-xl);
  color: var(--color-white-70);
  min-height: 1.5em;
}

.spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--gap-lg);
}

.dot {
  width: var(--spinner-dot-size);
  height: var(--spinner-dot-size);
  border-radius: var(--border-radius-circle);
  background: var(--gradient-success);
  animation: spin var(--animation-duration-slow) infinite;
}

.dot:nth-child(2) {
  animation-delay: var(--animation-delay-1);
}

.dot:nth-child(3) {
  animation-delay: var(--animation-delay-2);
}

.dot:nth-child(4) {
  animation-delay: var(--animation-delay-3);
}

@keyframes spin {

  0%,
  100% {
    transform: scale(0.3);
    opacity: 0.3;
  }

  50% {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .loader-wrapper {
    width: min(100%, 320px);
    padding-top: 12px;
  }

  .loader-copy {
    min-height: 146px;
    grid-template-rows: minmax(34px, auto) minmax(72px, auto);
  }

  .progress-placeholder {
    width: min(240px, 100%);
  }
}
</style>
